import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MapPin, Sun, Shield, TreePine, ShoppingBag, Utensils, Mountain, GraduationCap, BookOpen, Trophy, Ticket, Map, Heart, Car, Plane } from 'lucide-react';
import { ContactCTA } from '../components/ContactCTA';
import { ListingsComingSoon } from '../components/ListingsComingSoon';
import { SEOHead } from '../components/SEOHead';
import { RelatedCommunities } from '../components/RelatedCommunities';
export function SummerlinPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <main className="bg-white" data-id="element-2008">
      <SEOHead title="Summerlin Real Estate & Luxury Homes for Sale | Jenny Gong" description="Explore luxury homes for sale in Summerlin, Las Vegas's premier master-planned community. Discover exclusive estates, top schools, and world-class amenities." keywords="Summerlin real estate, Summerlin homes for sale, luxury homes Summerlin Las Vegas, The Ridges real estate, Summerlin realtor" canonicalUrl="https://jennygong.com/summerlin" ogImage="https://images.unsplash.com/photo-1512917774080-9991f1c4c750?q=80&w=2940&auto=format&fit=crop" data-id="element-2009" />
      {/* Hero Section */}
      <section className="relative h-[70vh] min-h-[600px] w-full flex items-center justify-center overflow-hidden" data-id="element-2010">
        <div className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat" style={{
        backgroundImage: 'url("https://images.unsplash.com/photo-1512917774080-9991f1c4c750?q=80&w=2940&auto=format&fit=crop")'
      }} data-id="element-2011">
          <div className="absolute inset-0 bg-charcoal/40 mix-blend-multiply" data-id="element-2012"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-charcoal/60 via-transparent to-charcoal/80" data-id="element-2013"></div>
        </div>

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto mt-20" data-id="element-2014">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1
        }} data-id="element-2015">
            <span className="text-gold uppercase tracking-[0.3em] text-sm font-medium mb-6 block" data-id="element-2016">
              Community Spotlight
            </span>
            <h1 className="text-5xl md:text-7xl text-white font-serif mb-6 leading-tight" data-id="element-2017">
              Summerlin Homes for Sale
            </h1>
            <p className="text-lg md:text-xl text-white/90 font-light max-w-2xl mx-auto" data-id="element-2018">
              Las Vegas's premier master-planned community, nestled against the
              stunning Red Rock Canyon — luxury living at its finest.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Overview & Stats */}
      <section className="py-24 px-6 bg-white" data-id="element-2019">
        <div className="max-w-7xl mx-auto" data-id="element-2020">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-2021">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-2022">
              <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-6" data-id="element-2023">
                The Pinnacle of{' '}
                <span className="italic text-gold" data-id="element-2024">
                  Summerlin Luxury Living
                </span>
              </h2>
              <div className="space-y-4 text-charcoal-light font-light leading-relaxed text-lg" data-id="element-2025">
                <p data-id="element-2026">
                  Developed by The Howard Hughes Corporation starting in 1990,
                  Summerlin spans 22,500 acres along the western rim of the Las
                  Vegas Valley. It is consistently ranked among the nation's
                  top-selling and most desirable master-planned communities.
                </p>
                <p data-id="element-2027">
                  Home to over 100,000 residents across 30+ distinct villages,
                  Summerlin offers an unparalleled lifestyle that perfectly
                  balances natural beauty with urban sophistication. It is
                  widely recognized as one of the safest and most
                  well-maintained communities in Nevada, rivaling{' '}
                  <Link to="/henderson" className="text-gold hover:underline" data-id="element-2028">
                    Henderson
                  </Link>{' '}
                  and{' '}
                  <Link to="/southern-highlands" className="text-gold hover:underline" data-id="element-2029">
                    Southern Highlands
                  </Link>{' '}
                  for top-tier living.
                </p>
                <p data-id="element-2030">
                  The real estate landscape is incredibly diverse, offering
                  everything from beautiful $400K starter homes and stylish
                  townhomes to breathtaking $10M+ custom estates perched on the
                  mountainside.
                </p>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="grid grid-cols-2 gap-6" data-id="element-2031">
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-2032">
                <TreePine className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-2033" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-2034">250+</h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-2035">
                  Parks & Trails
                </p>
              </div>
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-2036">
                <Sun className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-2037" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-2038">300</h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-2039">
                  Days of Sun
                </p>
              </div>
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-2040">
                <Shield className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-2041" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-2042">Top</h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-2043">
                  Rated Schools
                </p>
              </div>
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-2044">
                <MapPin className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-2045" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-2046">15m</h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-2047">
                  To The Strip
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Downtown Summerlin */}
      <section className="py-24 bg-warmGray px-6 border-y border-warmGray-dark" data-id="element-2048">
        <div className="max-w-7xl mx-auto" data-id="element-2049">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-2050">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="order-2 lg:order-1" data-id="element-2051">
              <div className="relative h-[500px]" data-id="element-2052">
                <img src="https://images.unsplash.com/photo-1519999482648-25049ddd37b1?q=80&w=2126&auto=format&fit=crop" alt="Downtown Summerlin open-air shopping and dining district in Las Vegas" className="w-full h-full object-cover" data-id="element-2053" />
                <div className="absolute inset-0 border border-gold/30 m-4 pointer-events-none" data-id="element-2054"></div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="order-1 lg:order-2" data-id="element-2055">
              <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-2056">
                The Heart of the Community
              </span>
              <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-6" data-id="element-2057">
                Downtown Summerlin
              </h2>
              <p className="text-charcoal-light font-light leading-relaxed text-lg mb-8" data-id="element-2058">
                A vibrant 1.6 million square foot open-air shopping, dining, and
                entertainment district that serves as the urban core of the
                community.
              </p>

              <div className="space-y-6" data-id="element-2059">
                <div className="flex items-start gap-4" data-id="element-2060">
                  <ShoppingBag className="w-6 h-6 text-gold flex-shrink-0 mt-1" data-id="element-2061" />
                  <div data-id="element-2062">
                    <h4 className="font-serif text-xl text-charcoal mb-1" data-id="element-2063">
                      Premier Shopping
                    </h4>
                    <p className="text-charcoal-light font-light text-sm" data-id="element-2064">
                      Over 125 stores including major retailers like Nordstrom,
                      Macy's, and Dillard's, alongside boutique shops.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-2065">
                  <Utensils className="w-6 h-6 text-gold flex-shrink-0 mt-1" data-id="element-2066" />
                  <div data-id="element-2067">
                    <h4 className="font-serif text-xl text-charcoal mb-1" data-id="element-2068">
                      World-Class Dining
                    </h4>
                    <p className="text-charcoal-light font-light text-sm" data-id="element-2069">
                      A diverse culinary scene ranging from casual cafes and
                      weekly farmers markets to upscale fine dining experiences.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-2070">
                  <Ticket className="w-6 h-6 text-gold flex-shrink-0 mt-1" data-id="element-2071" />
                  <div data-id="element-2072">
                    <h4 className="font-serif text-xl text-charcoal mb-1" data-id="element-2073">
                      Entertainment
                    </h4>
                    <p className="text-charcoal-light font-light text-sm" data-id="element-2074">
                      Features a luxury movie theater, fitness studios, and The
                      Pavilion — an outdoor event space hosting concerts and
                      holiday events.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Sports & Entertainment */}
      <section className="py-24 px-6 bg-white" data-id="element-2075">
        <div className="max-w-7xl mx-auto" data-id="element-2076">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="text-center mb-16" data-id="element-2077">
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-2078">
              World-Class Venues
            </span>
            <h2 className="text-4xl font-serif text-charcoal" data-id="element-2079">
              Sports & Entertainment
            </h2>
            <p className="text-charcoal-light font-light max-w-2xl mx-auto mt-4" data-id="element-2080">
              Summerlin isn't just a residential area; it's a major hub for
              professional sports and luxury entertainment in Las Vegas.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2081">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="group" data-id="element-2082">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-2083">
                <img src="https://images.unsplash.com/photo-1508344928928-7165b67de128?q=80&w=2940&auto=format&fit=crop" alt="Las Vegas Ballpark stadium in Downtown Summerlin home of the Aviators" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-2084" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-2085">
                Las Vegas Ballpark
              </h3>
              <p className="text-charcoal-light font-light text-sm" data-id="element-2086">
                Home of the Las Vegas Aviators (Triple-A affiliate of the
                Oakland Athletics). This state-of-the-art 10,000-seat stadium
                offers an incredible game day experience right in Downtown
                Summerlin.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="group" data-id="element-2087">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-2088">
                <img src="https://images.unsplash.com/photo-1580748141549-71748dbe0bdc?q=80&w=2787&auto=format&fit=crop" alt="City National Arena ice rink in Summerlin home of Vegas Golden Knights practice facility" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-2089" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-2090">
                City National Arena
              </h3>
              <p className="text-charcoal-light font-light text-sm" data-id="element-2091">
                The official practice facility for the Vegas Golden Knights NHL
                team. It features two ice sheets and is open to the community
                for public skating, youth leagues, and events.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} className="group" data-id="element-2092">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-2093">
                <img src="https://images.unsplash.com/photo-1566073771259-6a8506099945?q=80&w=2940&auto=format&fit=crop" alt="Red Rock Resort and Casino luxury spa and dining in Summerlin Las Vegas" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-2094" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-2095">
                Red Rock Resort
              </h3>
              <p className="text-charcoal-light font-light text-sm" data-id="element-2096">
                A stunning luxury resort and casino offering a world-class spa,
                bowling alley, movie theater, and multiple award-winning
                restaurants, serving as a playground for locals.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Outdoor Living */}
      <section className="py-24 bg-warmGray px-6 border-y border-warmGray-dark" data-id="element-2097">
        <div className="max-w-7xl mx-auto" data-id="element-2098">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-2099">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="space-y-8" data-id="element-2100">
              <div data-id="element-2101">
                <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-2102">
                  Active Lifestyle
                </span>
                <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-6" data-id="element-2103">
                  Outdoor Recreation
                </h2>
                <p className="text-charcoal-light font-light leading-relaxed text-lg" data-id="element-2104">
                  Summerlin was designed for those who love the outdoors,
                  offering an unmatched array of recreational amenities set
                  against a stunning desert backdrop.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-2105">
                <div className="bg-white p-6 border border-warmGray-dark" data-id="element-2106">
                  <Mountain className="w-6 h-6 text-gold mb-3" data-id="element-2107" />
                  <h4 className="font-serif text-lg text-charcoal mb-2" data-id="element-2108">
                    Red Rock Canyon
                  </h4>
                  <p className="text-charcoal-light font-light text-sm" data-id="element-2109">
                    Just minutes away, offering world-class hiking, rock
                    climbing, and a 13-mile scenic drive.
                  </p>
                </div>
                <div className="bg-white p-6 border border-warmGray-dark" data-id="element-2110">
                  <Map className="w-6 h-6 text-gold mb-3" data-id="element-2111" />
                  <h4 className="font-serif text-lg text-charcoal mb-2" data-id="element-2112">
                    Trail System
                  </h4>
                  <p className="text-charcoal-light font-light text-sm" data-id="element-2113">
                    Over 150 miles of interconnected trails perfect for walking,
                    running, and cycling.
                  </p>
                </div>
                <div className="bg-white p-6 border border-warmGray-dark" data-id="element-2114">
                  <TreePine className="w-6 h-6 text-gold mb-3" data-id="element-2115" />
                  <h4 className="font-serif text-lg text-charcoal mb-2" data-id="element-2116">
                    250+ Parks
                  </h4>
                  <p className="text-charcoal-light font-light text-sm" data-id="element-2117">
                    Including major community hubs like Willows Park, The Vistas
                    Park, and Arbors Tennis Park.
                  </p>
                </div>
                <div className="bg-white p-6 border border-warmGray-dark" data-id="element-2118">
                  <Trophy className="w-6 h-6 text-gold mb-3" data-id="element-2119" />
                  <h4 className="font-serif text-lg text-charcoal mb-2" data-id="element-2120">
                    Championship Golf
                  </h4>
                  <p className="text-charcoal-light font-light text-sm" data-id="element-2121">
                    9 spectacular courses including TPC Las Vegas, Bear's Best,
                    and Angel Park Golf Club.
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-2122">
              <div className="relative h-[600px]" data-id="element-2123">
                <img src="https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?q=80&w=2842&auto=format&fit=crop" alt="Red Rock Canyon hiking trails near Summerlin Las Vegas luxury homes" className="w-full h-full object-cover" data-id="element-2124" />
                <div className="absolute inset-0 border border-gold/30 m-4 pointer-events-none" data-id="element-2125"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Education */}
      <section className="py-24 px-6 bg-white" data-id="element-2126">
        <div className="max-w-7xl mx-auto" data-id="element-2127">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-2128">
            <GraduationCap className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-2129" />
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-2130">
              Education Focus
            </span>
            <h2 className="text-4xl font-serif text-charcoal mb-6" data-id="element-2131">
              Summerlin Schools & Education
            </h2>
            <p className="text-charcoal-light font-light max-w-3xl mx-auto text-lg leading-relaxed" data-id="element-2132">
              Education is a cornerstone of the Summerlin community. It is home
              to some of the highest-rated public schools in the Clark County
              School District, alongside prestigious private academies.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2133">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-warmGray p-8 border border-warmGray-dark" data-id="element-2134">
              <h3 className="text-xl font-serif text-charcoal mb-4 border-b border-gold/20 pb-4" data-id="element-2135">
                Elementary & Middle
              </h3>
              <ul className="space-y-3 text-charcoal-light font-light text-sm" data-id="element-2136">
                <li data-id="element-2137">• Staton Elementary School</li>
                <li data-id="element-2138">• Goolsby Elementary School</li>
                <li data-id="element-2139">• Lummis Elementary School</li>
                <li data-id="element-2140">• Sig Rogich Middle School</li>
                <li data-id="element-2141">• Ernest Becker Middle School</li>
                <li data-id="element-2142">• Victoria Fertitta Middle School</li>
              </ul>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-warmGray p-8 border border-warmGray-dark" data-id="element-2143">
              <h3 className="text-xl font-serif text-charcoal mb-4 border-b border-gold/20 pb-4" data-id="element-2144">
                High Schools
              </h3>
              <ul className="space-y-3 text-charcoal-light font-light text-sm" data-id="element-2145">
                <li data-id="element-2146">• Palo Verde High School</li>
                <li data-id="element-2147">• West Career and Technical Academy</li>
                <li data-id="element-2148">• Spring Valley High School</li>
                <li className="pt-4 italic text-xs text-gold" data-id="element-2149">
                  Palo Verde is consistently ranked among the top public high
                  schools in Nevada.
                </li>
              </ul>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="bg-warmGray p-8 border border-warmGray-dark" data-id="element-2150">
              <h3 className="text-xl font-serif text-charcoal mb-4 border-b border-gold/20 pb-4" data-id="element-2151">
                Private & Charter
              </h3>
              <ul className="space-y-3 text-charcoal-light font-light text-sm" data-id="element-2152">
                <li data-id="element-2153">• The Meadows School (Pre-K to 12)</li>
                <li data-id="element-2154">• Faith Lutheran Middle & High School</li>
                <li data-id="element-2155">• Bishop Gorman High School (Nearby)</li>
                <li data-id="element-2156">• Alexander Dawson School</li>
                <li data-id="element-2157">• Doral Academy Red Rock</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Healthcare */}
      <section className="py-24 bg-warmGray px-6 border-t border-warmGray-dark" data-id="element-2158">
        <div className="max-w-4xl mx-auto text-center" data-id="element-2159">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-2160">
            <Heart className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-2161" />
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-2162">
              Wellness
            </span>
            <h2 className="text-4xl font-serif text-charcoal mb-6" data-id="element-2163">
              Summerlin Healthcare & Wellness
            </h2>
            <p className="text-charcoal-light font-light leading-relaxed text-lg mb-8" data-id="element-2164">
              Summerlin residents have access to exceptional medical care right
              in their backyard. The community is anchored by{' '}
              <strong data-id="element-2165">Summerlin Hospital Medical Center</strong>, a
              comprehensive 485-bed facility known for its advanced robotic
              surgery, pediatric center, and comprehensive cancer center.
            </p>
            <p className="text-charcoal-light font-light leading-relaxed text-lg" data-id="element-2166">
              The area also features dozens of specialized medical plazas,
              wellness centers, and urgent care facilities seamlessly integrated
              into the community's master plan.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Transportation */}
      <section className="py-24 bg-charcoal px-6 text-white" data-id="element-2167">
        <div className="max-w-7xl mx-auto" data-id="element-2168">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-2169">
            <MapPin className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-2170" />
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-2171">
              Connectivity
            </span>
            <h2 className="text-4xl font-serif mb-6" data-id="element-2172">
              Summerlin Transportation & Access
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-2173">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-white/5 p-8 border border-white/10 text-center" data-id="element-2174">
              <Car className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-2175" />
              <h4 className="text-xl font-serif mb-2" data-id="element-2176">Summerlin Parkway</h4>
              <p className="text-white/60 font-light text-sm" data-id="element-2177">
                A dedicated expressway providing a direct, high-speed connection
                from Summerlin to US-95 and downtown.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-white/5 p-8 border border-white/10 text-center" data-id="element-2178">
              <MapPin className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-2179" />
              <h4 className="text-xl font-serif mb-2" data-id="element-2180">215 Beltway</h4>
              <p className="text-white/60 font-light text-sm" data-id="element-2181">
                Wraps around the entire western edge of the valley, connecting
                Summerlin to the airport and{' '}
                <Link to="/henderson" className="text-gold hover:underline" data-id="element-2182">
                  Henderson
                </Link>
                .
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="bg-white/5 p-8 border border-white/10 text-center" data-id="element-2183">
              <Plane className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-2184" />
              <h4 className="text-xl font-serif mb-2" data-id="element-2185">Harry Reid Airport</h4>
              <p className="text-white/60 font-light text-sm" data-id="element-2186">
                Approximately a 20-25 minute drive via the 215 Beltway, offering
                easy access for frequent travelers.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.4
          }} className="bg-white/5 p-8 border border-white/10 text-center" data-id="element-2187">
              <MapPin className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-2188" />
              <h4 className="text-xl font-serif mb-2" data-id="element-2189">The Strip</h4>
              <p className="text-white/60 font-light text-sm" data-id="element-2190">
                Just 15-20 minutes away, allowing residents to easily access
                world-class entertainment and return to suburban peace.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Neighborhood Highlights */}
      <section className="py-24 bg-white px-6 border-t border-warmGray-dark" data-id="element-2191">
        <div className="max-w-7xl mx-auto" data-id="element-2192">
          <div className="text-center mb-16" data-id="element-2193">
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-2194">
              Exclusive Enclaves
            </span>
            <h2 className="text-4xl font-serif text-charcoal" data-id="element-2195">
              Summerlin Villages & Neighborhoods
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-2196">
            {[{
            name: 'The Ridges',
            price: '$1M - $10M+',
            desc: 'Ultra-luxury custom homes built into the mountainside with a private Jack Nicklaus golf course and stunning Strip views.',
            img: 'https://images.unsplash.com/photo-1613490908571-9ce224083933?q=80&w=2838&auto=format&fit=crop'
          }, {
            name: 'The Summit Club',
            price: '$3M - $30M+',
            desc: 'The most exclusive private golf and lifestyle community in Las Vegas, offering unmatched luxury and a Tom Fazio course.',
            img: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?q=80&w=2950&auto=format&fit=crop'
          }, {
            name: 'Red Rock Country Club',
            price: '$500K - $2M+',
            desc: 'Guard-gated community featuring two Arnold Palmer golf courses, a luxurious clubhouse, and stunning canyon views.',
            img: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?q=80&w=2906&auto=format&fit=crop'
          }, {
            name: 'The Paseos',
            price: '$400K - $700K',
            desc: 'A highly sought-after, family-friendly village featuring excellent parks, top schools, and beautiful elevated views.',
            img: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=2940&auto=format&fit=crop'
          }, {
            name: 'Reverence',
            price: '$500K - $1.2M',
            desc: 'A newer luxury development by Pulte Homes offering modern architecture, a private clubhouse, and mountain views.',
            img: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?q=80&w=2940&auto=format&fit=crop'
          }, {
            name: 'Stonebridge',
            price: '$450K - $800K',
            desc: 'An established neighborhood known for its mature landscaping, strong community feel, and proximity to Red Rock.',
            img: 'https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?q=80&w=2792&auto=format&fit=crop'
          }].map((village, idx) => <motion.div key={village.name} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} className="bg-white p-6 shadow-sm border border-warmGray-dark" data-id="element-2197">
                <img src={village.img} alt={`${village.name} luxury homes for sale in Summerlin Las Vegas from ${village.price}`} className="w-full h-48 object-cover mb-4" data-id="element-2198" />
                <div className="flex justify-between items-start mb-3" data-id="element-2199">
                  <h3 className="text-xl font-serif text-charcoal" data-id="element-2200">
                    {village.name}
                  </h3>
                  <span className="text-xs font-medium text-gold bg-gold/10 px-2 py-1 rounded" data-id="element-2201">
                    {village.price}
                  </span>
                </div>
                <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-2202">
                  {village.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      <RelatedCommunities currentCommunity="Summerlin" data-id="element-2203" />

      <ListingsComingSoon area="Summerlin" data-id="element-2204" />

      <ContactCTA data-id="element-2205" />
    </main>;
}